import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { AnimatePresence, motion } from 'motion/react'
import {
  Feature,
  Project,
  SummaryConfig,
  SummaryExplainRegister,
  SummaryNumericResponse,
  SummaryWindow,
  explainSummary,
  getSummaryActiveDates,
  getSummaryNumeric,
} from '../../api/client'
import SummaryConfigMenu from './SummaryConfigMenu'
import { EditorSurface } from './SummaryConfigEditor'
import { useSummaryCardPrefs } from './useSummaryCardPrefs'
import {
  type Granularity,
  type TimeRangeState,
  isoToLocalMs,
  useTimeRangeState,
  windowForAnchor,
} from '../../charts/windowRange'
import ChartTimeRangePicker from '../../charts/ChartTimeRangePicker'
import { boolDaySeries } from '../../charts/bodies/boolAggregation'
import {
  SUMMARY_VIEW_BODIES,
  SUMMARY_VIEW_ICONS,
  SUMMARY_VIEW_LABELS,
  SUMMARY_VIEW_SKELETON,
  SUMMARY_VIEW_SOURCES,
  defaultGranularityFor,
  viewsForConfig,
  windowsForConfig,
  type SummaryViewId,
} from './viewRegistry'

/**
 * FeatureSummaryCard — the per-config text+numeric summary surface rendered on
 * the feature detail page (Stage 6).
 *
 * Period navigation mirrors the feature chart's iOS-Health-style picker
 * (`ChartTimeRangePicker` + `useTimeRangeState`): a W/M/6M/Y/All granularity
 * row and « ← label → » paging that skips empty windows. The window math is
 * shared with the charts via `charts/windowRange`, and the populated-date list
 * driving skip-empty paging comes from `GET /api/summaries/active-dates`.
 *
 * For the selected window it fetches:
 *  - the LIVE numeric panel via `GET /api/summaries/numeric?window=…` (always
 *    computed),
 *  - the precomputed text row via `GET /api/summaries/results` — ONLY for the
 *    three text-bearing tiers (week/month/year). 6M and All are numeric-only.
 */

// Picker granularity → backend numeric window, and the precomputed-text tier it
// maps to (null = numeric-only, no text summary).
const GRANULARITY_TO_WINDOW: Record<Granularity, SummaryWindow> = {
  W: 'week',
  M: 'month',
  '6M': '6m',
  Y: 'year',
  All: 'all',
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** The window's start as a local ISO date — what the numeric endpoint snaps
 *  from. 'All' has no finite start, so the backend ignores it; send today. */
function windowStartIso(granularity: Granularity, anchor: Date): string {
  const win = windowForAnchor(granularity, anchor)
  if (!Number.isFinite(win.start)) return isoOf(new Date())
  return isoOf(new Date(win.start))
}

/** Placeholder cell mirroring StatCell's box and line heights (label line =
 *  text-xs/16px, value line = text-lg/28px) so the loading grid is the exact
 *  same height as the populated NumericPanel and nothing shifts on load. */
const SkeletonCell: React.FC = () => (
  <div className="flex flex-col rounded-md bg-gray-50 px-3 py-2">
    <span className="flex h-4 items-center">
      <span className="h-2.5 w-12 rounded bg-gray-200" />
    </span>
    <span className="flex h-7 items-center">
      <span className="h-4 w-10 rounded bg-gray-300" />
    </span>
  </div>
)

interface FeatureSummaryCardProps {
  config: SummaryConfig
  /** The feature this card sits under — used to build a friendly
   *  "name (unit)" title instead of the technical builder label. */
  featureName?: string
  unit?: string
  /** Settings-gear context. When `surface` is set, a gear renders to the right
   *  of the view flipper, opening this config's Edit / Delete menu. Omitted in
   *  contexts (e.g. unit tests) that don't wire the editor. */
  surface?: EditorSurface
  featureId?: number
  projectId?: number
  features?: Feature[]
  /** Selectable projects for the editor's project-tag picker (dashboard). */
  projects?: Project[]
  /** Notified after an edit / delete so the host can refresh its cards. */
  onChanged?: () => void
  /** A drag handle, rendered leftmost in the card's corner controls beside the
   *  view switcher and gear. Supplied by a host that makes its cards sortable
   *  (the dashboard); omitted everywhere else, which is what leaves the card
   *  undraggable rather than draggable-with-no-effect. It lives HERE, with the
   *  card's other controls, rather than floating over the card: the corner is
   *  the only spot that doesn't overlap the title or the period picker. */
  dragHandle?: React.ReactNode
}

const FeatureSummaryCard: React.FC<FeatureSummaryCardProps> = ({
  config,
  featureName,
  unit,
  surface,
  featureId,
  projectId,
  features,
  projects,
  onChanged,
  dragHandle,
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [numeric, setNumeric] = useState<SummaryNumericResponse | null>(null)

  // Everything the card fetches is a function of the whole config, not just its
  // id: saving the settings (kind, sources, params such as `count` or
  // `day_rule`) hands this card a new config object, and the card must redraw
  // from it. Keyed on content, so the host's reload returning an identical
  // config refetches nothing.
  const configKey = JSON.stringify(config)

  // The config's days: `activeDates` are what the calendar / trend views draw
  // (on a yes/no streak or rate, only the counted days); `pagingDates` are the
  // days with any data, which drive skip-empty paging (shared chart picker
  // model) — a period whose answers are all uncounted is a real 0%, not empty.
  const [activeDates, setActiveDates] = useState<number[]>([])
  const [pagingDates, setPagingDates] = useState<number[]>([])
  useEffect(() => {
    let cancelled = false
    getSummaryActiveDates(config.id)
      .then(({ dates, pagingDates }) => {
        if (cancelled) return
        setActiveDates(dates.map(isoToLocalMs))
        setPagingDates(pagingDates.map(isoToLocalMs))
      })
      .catch(() => {
        if (cancelled) return
        setActiveDates([])
        setPagingDates([])
      })
    return () => {
      cancelled = true
    }
  }, [config.id, configKey])

  // How this card was last left — the remembered view and period granularity,
  // per config and server-side (`useSummaryCardPrefs`, the summary analogue of
  // the feature chart's `useChartPrefs`). Both are resolved here, against what
  // this config can currently offer, so a stored value the config outgrew
  // degrades to the default rather than to a blank card or a hidden tab.
  const cardPrefs = useSummaryCardPrefs()
  const storedPref = cardPrefs.get(config.id)

  // Allowed picker granularities for this config. Most kinds allow all five;
  // period_over_period drops '6M'/'All', and a streaks config narrows by cadence
  // (weekly drops 'W', monthly drops 'W'/'M' — see windowsForConfig).
  const allowedGranularities = React.useMemo(() => windowsForConfig(config), [config])

  // The granularity the card OPENS on: the remembered one while this config
  // still offers it, else the kind's default (itself narrowed to the allowed
  // set, so the picker never sits in a selected-but-hidden state). Only the
  // granularity is remembered, never the anchor — the hook keeps snapping that
  // to the latest populated window, which is what lands a card whose data
  // stopped months ago on data instead of on an empty current month.
  //
  // This is a plain prop rather than an override pushed into the hook: the prefs
  // map arrives after mount, and `setRange` would count as a user action and
  // stop that anchor snap. `useTimeRangeState` adopts a late `initialGranularity`
  // until the user works the picker.
  const openingGranularity = React.useMemo<Granularity>(() => {
    const stored = storedPref.granularity as Granularity | undefined
    if (stored && allowedGranularities.includes(stored)) return stored
    const fallback = defaultGranularityFor(config.kind)
    return allowedGranularities.includes(fallback) ? fallback : allowedGranularities[0]
  }, [storedPref.granularity, allowedGranularities, config.kind])

  const range = useTimeRangeState(pagingDates, openingGranularity)
  const { granularity, anchor } = range

  // A config edited to a narrower set (a cadence change dropping 'W'/'M') can
  // leave the ACTIVE granularity outside it, which the opening value above can't
  // fix once the user has worked the picker. Snap it back.
  useEffect(() => {
    if (!allowedGranularities.includes(granularity)) {
      range.setRange(allowedGranularities[0], anchor)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedGranularities, granularity])

  // The picker's own state: the range hook, with `setRange` wrapped to remember
  // the span the user picks. The granularity tabs are the only entry point that
  // changes it (the paging arrows move the anchor, which we don't remember), and
  // wrapping it HERE rather than in the hook means the snap-back above — a
  // correction the card makes on the user's behalf — isn't recorded as a choice.
  const pickerState: TimeRangeState = {
    ...range,
    setRange: (g, a) => {
      range.setRange(g, a)
      if (g !== granularity) void cardPrefs.set(config.id, { granularity: g })
    },
  }

  const window = GRANULARITY_TO_WINDOW[granularity]
  const periodStart = windowStartIso(granularity, anchor)

  // Active visualization for this card. Each kind exposes an ordered list of
  // views (default first); period_over_period has ['stats','line'], every
  // other kind is single-view. Remembered alongside the granularity above, so a
  // card reopens on the view you left it on, from any device. Local state still
  // leads so the switch is instant; the pref is written behind it.
  const views = React.useMemo(() => viewsForConfig(config), [config])
  const preferredView =
    storedPref.view && (views as string[]).includes(storedPref.view)
      ? (storedPref.view as SummaryViewId)
      : views[0]
  const [activeView, setActiveView] = useState<SummaryViewId>(preferredView)
  // Adopt the stored view ONCE per config, as soon as the prefs map lands (it
  // isn't there on the first render). After that this card owns its view: the
  // shared prefs cache changing again — this card's own write echoing back, or a
  // sibling card writing its own — must not yank the view out from under you.
  // The guard also carries the kind, so editing a config's kind re-resolves
  // against the new view cycle instead of keeping a view that cycle lacks.
  //
  // The granularity needs no such guard: it's a prop to `useTimeRangeState`,
  // which already stops adopting it the moment the user works the picker.
  const adoptedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!cardPrefs.loaded) return
    const key = `${config.id}:${config.kind}`
    if (adoptedFor.current === key) return
    adoptedFor.current = key
    setActiveView(preferredView)
  }, [cardPrefs.loaded, config.id, config.kind, preferredView])

  const selectView = (v: SummaryViewId) => {
    setActiveView(v)
    void cardPrefs.set(config.id, { view: v })
  }
  // The prose narrative is hidden behind an "Explain" toggle (collapsed by
  // default) and generated ON DEMAND, per (view, window, register): clicking
  // Explain asks the LLM to describe the visualization the user is currently
  // looking at over the period they've paged to. The UI defaults to the 'plain'
  // (non-technical) register; a toggle switches to the 'technical' (number-
  // dense) one. Results are cached by `${view}|${window}|${periodStart}|
  // ${register}` so revisiting a cell is instant and never re-hits the LLM.
  // `explainErrorKey` marks the one cell that failed (so we don't auto-retry it
  // and don't show a stale error on a different cell).
  const [explainOpen, setExplainOpen] = useState(false)
  const [explainRegister, setExplainRegister] = useState<SummaryExplainRegister>('plain')
  const [explainCache, setExplainCache] = useState<Record<string, string | null>>({})
  const [explainErrorKey, setExplainErrorKey] = useState<string | null>(null)
  useEffect(() => {
    setExplainOpen(false)
    setExplainRegister('plain')
    setExplainCache({})
    setExplainErrorKey(null)
  }, [config.id, configKey])

  // The next view in the cycle — what the corner switcher advances to (and
  // whose icon/label it shows, mirroring CyclerButton's "show what you're
  // about to see" convention).
  const viewIdx = views.indexOf(activeView)
  const nextView = views[(viewIdx + 1) % views.length]
  // The switcher label is per-view-id, but the shared 'calendar' body reads as a
  // "Streak" for streaks and a coverage heatmap for logging_rate — so relabel it
  // per kind rather than showing "Streak" on a rate card.
  const nextViewLabel =
    nextView === 'calendar' && config.kind === 'logging_rate'
      ? 'Coverage'
      : SUMMARY_VIEW_LABELS[nextView]

  // The body for the active view, and the current period's ms bounds (the same
  // half-open [start, end) window the picker resolves) handed to it. The stats
  // body ignores the window; the line body (Stage 5) filters its timeseries to
  // it. 'All' has no finite bounds — fine, since no kind exposes both 'All' and
  // a window-consuming view.
  const Body = SUMMARY_VIEW_BODIES[activeView]
  const dateWindow = windowForAnchor(granularity, anchor)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const num = await getSummaryNumeric({ configId: config.id, window, periodStart })
        if (cancelled) return
        setNumeric(num)
      } catch (e) {
        if (cancelled) return
        console.error('Failed to load feature summary', e)
        setError('Could not load this summary.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // Keyed on the derived (window, periodStart): paging to a different period
    // refetches, but snapping the anchor WITHIN the same window does not. A
    // settings save changes `configKey` and refetches too.
  }, [config.id, configKey, window, periodStart])

  // The active view's declared data sources (see SUMMARY_VIEW_SOURCES). The
  // card fetches every source a view needs so no body fetches its own data.
  const needsTimeseries = SUMMARY_VIEW_SOURCES[activeView].includes('timeseries')
  // The feature whose raw series the active view needs, read from the numeric
  // payload (distinct from the `featureId` prop, which is the page context the
  // settings gear edits against — usually the same value).
  const seriesFeatureId =
    typeof numeric?.numeric_payload?.feature_id === 'number'
      ? numeric.numeric_payload.feature_id
      : undefined

  // The raw feature series, fetched only when the active view needs it.
  // `null` = not loaded yet (or not needed); `[]` = loaded but empty. The
  // distinction lets the loading gate below tell "still fetching" from "this
  // feature has no points", so the line view never flashes an empty chart.
  const [timeseries, setTimeseries] = useState<Array<{ date: number; value: number }> | null>(null)
  useEffect(() => {
    if (!needsTimeseries || seriesFeatureId === undefined) return
    let cancelled = false
    setTimeseries(null)
    axios
      .get(`/api/features/timeseries/${seriesFeatureId}`)
      .then((r) => {
        if (cancelled) return
        const items = (r.data as Array<{ date: string; value: number }>) ?? []
        setTimeseries(items.map((i) => ({ value: i.value, date: isoToLocalMs(i.date) })))
      })
      .catch((e) => {
        console.error('Failed to load summary timeseries', e)
        if (!cancelled) setTimeseries([])
      })
    return () => {
      cancelled = true
    }
    // Keyed on seriesFeatureId (+ whether the active view needs it): the full
    // series is window-independent, so paging the period never refetches it.
  }, [needsTimeseries, seriesFeatureId])

  // Option (a): when the active view needs the raw series, the card's loading
  // gate covers that fetch too, so the body mounts only once its data is ready.
  // A scalar yes/no source plots one point per day under the summary's own
  // day rule (carried on the payload's `bool` block), not one per checkin —
  // otherwise a day with a "no" and a "yes" draws both and the line ends on
  // whichever sorted last.
  const boolDayRule = numeric?.numeric_payload?.bool?.day_rule as 'any' | 'all' | undefined
  const bodySeries = React.useMemo(
    () => (timeseries && boolDayRule ? boolDaySeries(timeseries, boolDayRule) : timeseries),
    [timeseries, boolDayRule],
  )

  const timeseriesPending = needsTimeseries && seriesFeatureId !== undefined && timeseries === null
  const showLoading = loading || timeseriesPending

  // STUB: a screenshot of the rendered "unit" (the chart/table) to send along
  // with the prompt. The backend already accepts an `image` field; capturing
  // the DOM (e.g. html-to-image over `unitRef`) and passing it here is future
  // work, so this returns undefined and no image is sent yet.
  const unitRef = useRef<HTMLDivElement>(null)
  const captureUnitImage = (): string | undefined => undefined

  // The explain cache cell for the current (view, window, period). When Explain
  // is open and this cell is neither cached nor known-failed, the effect below
  // generates it on demand. Cache stores `string | null` (null = the LLM
  // returned nothing); `explainKey in cache` distinguishes "fetched" from
  // "never asked".
  const explainKey = `${activeView}|${window}|${periodStart}|${explainRegister}`
  const explainHasCell = explainKey in explainCache
  const explainText = explainCache[explainKey]
  const explainBusy = explainOpen && !explainHasCell && explainErrorKey !== explainKey

  useEffect(() => {
    if (!explainOpen || explainHasCell || explainErrorKey === explainKey) return
    let cancelled = false
    explainSummary({
      configId: config.id,
      view: activeView,
      window,
      periodStart,
      register: explainRegister,
      image: captureUnitImage(),
    })
      .then((res) => {
        if (!cancelled) setExplainCache((prev) => ({ ...prev, [explainKey]: res.text }))
      })
      .catch((e) => {
        if (cancelled) return
        console.error('Failed to explain summary', e)
        setExplainErrorKey(explainKey)
      })
    return () => {
      cancelled = true
    }
  }, [explainOpen, explainKey, explainHasCell, explainErrorKey, config.id, activeView, window, periodStart, explainRegister])

  // Header: user_title is primary; otherwise a friendly "name (unit)" title
  // (e.g. "duration (mins)"), falling back to the builder label only when the
  // feature name isn't available.
  const friendlyTitle = featureName
    ? `${featureName}${unit ? ` (${unit})` : ''}`
    : numeric?.label ?? null
  const hasUserTitle = !!(config.user_title && config.user_title.trim())
  const headerText = hasUserTitle ? config.user_title! : friendlyTitle ?? 'Summary'
  const secondaryLabel = hasUserTitle ? friendlyTitle : null

  const noData = !showLoading && !error && (!numeric || numeric.is_empty)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={{ transformOrigin: 'top center' }}
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
      data-testid="feature-summary-card"
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{headerText}</h3>
          {secondaryLabel && (
            <p className="mt-0.5 text-xs text-gray-400">{secondaryLabel}</p>
          )}
          {config.user_description && (
            <p className="mt-1 text-sm text-gray-500">{config.user_description}</p>
          )}
        </div>
        {/* Corner controls: an optional drag handle, the view switcher (only when
            the kind has more than one view), and the settings gear (Edit /
            Delete). */}
        {(views.length > 1 || surface || dragHandle) && (
          <div className="flex shrink-0 items-start gap-1">
            {dragHandle}
            {/* View switcher — mirrors charts/CyclerButton: shows the NEXT
                view's icon, advances through the cycle on click. */}
            {views.length > 1 && (
              <div className="flex flex-col items-center gap-1">
                <motion.button
                  type="button"
                  onClick={() => selectView(nextView)}
                  title={`Show ${nextViewLabel}`}
                  aria-label={`Show ${nextViewLabel}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800"
                >
                  {SUMMARY_VIEW_ICONS[nextView]}
                </motion.button>
                {/* Subtle page dots: how many views this card has, and where we
                    are in the cycle. Decorative — the button carries the label. */}
                <div className="flex items-center gap-1" aria-hidden>
                  {views.map((v, i) => (
                    <span
                      key={v}
                      className={`h-1.5 w-1.5 rounded-full transition-colors ${
                        i === viewIdx ? 'bg-gray-500' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Settings gear — Edit / Delete for this summary's config. */}
            {surface && (
              <SummaryConfigMenu
                config={config}
                surface={surface}
                featureId={featureId}
                projectId={projectId}
                features={features}
                projects={projects}
                onChanged={onChanged}
              />
            )}
          </div>
        )}
      </div>

      <ChartTimeRangePicker state={pickerState} granularities={allowedGranularities} />

      {showLoading && (
        <div className="animate-pulse" data-testid="feature-summary-skeleton">
          {SUMMARY_VIEW_SKELETON[activeView] === 'chart' ? (
            // Chart views render in an h-64 box — reserve that height so the
            // card doesn't jump when the chart arrives.
            <div className="h-64 rounded-md bg-gray-100" />
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonCell key={i} />
              ))}
            </div>
          )}
        </div>
      )}

      {error && !showLoading && (
        <div className="py-4 text-sm text-rose-500">{error}</div>
      )}

      {!showLoading && !error && (
        <>
          {numeric && !numeric.is_empty && (
            <div ref={unitRef}>
              <Body
                numeric={numeric}
                config={config}
                featureName={featureName}
                unit={unit}
                granularity={granularity}
                window={dateWindow}
                timeseries={bodySeries ?? []}
                activeDates={activeDates}
                pagingDates={pagingDates}
              />
            </div>
          )}

          {/* On-demand per-view "Explain" — collapsed by default; clicking it
              generates a narrative for THIS view over the current window. */}
          {numeric && !numeric.is_empty && (
            <div className="mt-4">
              <motion.button
                type="button"
                onClick={() => setExplainOpen((o) => !o)}
                aria-expanded={explainOpen}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{ transformOrigin: 'left center' }}
                className="text-xs font-medium text-gray-400 transition-colors hover:text-gray-600"
              >
                {explainOpen ? 'Hide' : 'Explain'}
              </motion.button>
              <AnimatePresence initial={false}>
                {explainOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -8 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    style={{ transformOrigin: 'top center' }}
                    className="mt-2"
                  >
                    {explainBusy ? (
                      <div className="space-y-2" data-testid="summary-explain-loading">
                        <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                        <div className="h-3 w-11/12 animate-pulse rounded bg-gray-100" />
                        <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
                      </div>
                    ) : explainErrorKey === explainKey ? (
                      <p className="text-sm text-rose-500">Could not generate an explanation.</p>
                    ) : explainText ? (
                      <>
                        <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
                          {explainText}
                        </p>
                        {/* Register toggle — the UI defaults to the plain,
                            non-technical voice; this switches to the terse,
                            number-dense one (kept as an option). */}
                        <button
                          type="button"
                          onClick={() =>
                            setExplainRegister((r) => (r === 'plain' ? 'technical' : 'plain'))
                          }
                          className="mt-1.5 text-xs text-gray-400 transition-colors hover:text-gray-600"
                        >
                          {explainRegister === 'plain'
                            ? 'Show technical detail'
                            : 'Show simple version'}
                        </button>
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">
                        No explanation available for this period.
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {noData && (
            <div className="py-4 text-sm text-gray-400">No data for this period.</div>
          )}
        </>
      )}
    </motion.div>
  )
}

export default FeatureSummaryCard
